import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import Start from "./pages/Start";
import NotFound from "./pages/NotFound";
import { TrackingScripts } from "./components/TrackingScripts";
import { AuthProvider } from "./contexts/AuthContext";
import AuthPage from "./pages/AuthPage";

// Admin pages
import AdminLogin from "./pages/admin/AdminLogin";
import AnalyticsDashboard from "./pages/admin/AnalyticsDashboard";
import Generations from "./pages/admin/Generations";
import Users from "./pages/admin/Users";
import Events from "./pages/admin/Events";
import Genres from "./pages/admin/Genres";
import Prompts from "./pages/admin/Prompts";
import Transactions from "./pages/admin/Transactions";
import Referrals from "./pages/admin/Referrals";
import Stats from "./pages/admin/Stats";
import Settings from "./pages/admin/Settings";
import Funnel from "./pages/admin/Funnel";
import UTM from "./pages/admin/UTM";
import Feedback from "./pages/admin/Feedback";
import Vocals from "./pages/admin/Vocals";
import Timings from "./pages/admin/Timings";
import PromoCodes from "./pages/admin/PromoCodes";
import Analytics from "./pages/admin/Analytics";
import QRCampaigns from "./pages/admin/QRCampaigns";
import LandingReviews from "./pages/admin/LandingReviews";
import LandingFAQ from "./pages/admin/LandingFAQ";
import LandingSEO from "./pages/admin/LandingSEO";
import GiftPages from "./pages/admin/GiftPages";
import GiftAnalytics from "./pages/admin/GiftAnalytics";
import BotSimulator from "./pages/admin/BotSimulator";
import AudioPipeline from "./pages/admin/AudioPipeline";
import PostPurchaseAnalytics from "./pages/admin/PostPurchaseAnalytics";
import StorageStats from "./pages/admin/StorageStats";
import Broadcasts from "./pages/admin/Broadcasts";
import PesnyaNaDenRozhdeniya from "./pages/PesnyaNaDenRozhdeniya";
import PesnyaVPodarok from "./pages/PesnyaVPodarok";
import PesnyaProDruga from "./pages/PesnyaProDruga";
import PesnyaDlyaVecherinki from "./pages/PesnyaDlyaVecherinki";
import GiftPage from "./pages/GiftPage";
import AvitoLanding from "./pages/AvitoLanding";
import AdBirthdayLanding from "./pages/AdBirthdayLanding";
import Ad8MartaLanding from "./pages/Ad8MartaLanding";
import PesnyaDevushkeVPodarok from "./pages/PesnyaDevushkeVPodarok";
import PesnyaMameVPodarok from "./pages/PesnyaMameVPodarok";
import PesnyaNaSvadbu from "./pages/PesnyaNaSvadbu";
import PesnyaNaYubiley from "./pages/PesnyaNaYubiley";
import PesnyaNaVypusknoj from "./pages/PesnyaNaVypusknoj";
import PesnyaKollegeVPodarok from "./pages/PesnyaKollegeVPodarok";
import PesnyaRebenkuVPodarok from "./pages/PesnyaRebenkuVPodarok";
import PesnyaNaGodovshchinu from "./pages/PesnyaNaGodovshchinu";
import OriginalnyiPodarok from "./pages/OriginalnyiPodarok";
import PozdravlenieVStihah from "./pages/PozdravlenieVStihah";
import CreateSong from "./pages/CreateSong";
import AccountPage from "./pages/AccountPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ProtectedRoute from "./components/ProtectedRoute";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <TrackingScripts />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/create" element={<ProtectedRoute><CreateSong /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
          <Route path="/start" element={<Start />} />
          <Route path="/pesnya-na-den-rozhdeniya" element={<PesnyaNaDenRozhdeniya />} />
          <Route path="/pesnya-v-podarok" element={<PesnyaVPodarok />} />
          <Route path="/pesnya-pro-druga" element={<PesnyaProDruga />} />
          <Route path="/pesnya-dlya-vecherinki" element={<PesnyaDlyaVecherinki />} />
          <Route path="/g/:slug" element={<GiftPage />} />
          <Route path="/avito" element={<AvitoLanding />} />
          <Route path="/ad-birthday" element={<AdBirthdayLanding />} />
          <Route path="/ad-8marta" element={<Ad8MartaLanding />} />
          <Route path="/pesnya-devushke-v-podarok" element={<PesnyaDevushkeVPodarok />} />
          <Route path="/pesnya-mame-v-podarok" element={<PesnyaMameVPodarok />} />
          <Route path="/pesnya-na-svadbu" element={<PesnyaNaSvadbu />} />
          <Route path="/pesnya-na-yubiley" element={<PesnyaNaYubiley />} />
          <Route path="/pesnya-na-vypusknoj" element={<PesnyaNaVypusknoj />} />
          <Route path="/pesnya-kollege-v-podarok" element={<PesnyaKollegeVPodarok />} />
          <Route path="/pesnya-rebenku-v-podarok" element={<PesnyaRebenkuVPodarok />} />
          <Route path="/pesnya-na-godovshchinu" element={<PesnyaNaGodovshchinu />} />
          <Route path="/originalnyi-podarok" element={<OriginalnyiPodarok />} />
          <Route path="/pozdravlenie-v-stihah" element={<PozdravlenieVStihah />} />

          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AnalyticsDashboard />} />
          <Route path="/admin/generations" element={<Generations />} />
          <Route path="/admin/users" element={<Users />} />
          <Route path="/admin/events" element={<Events />} />
          <Route path="/admin/genres" element={<Genres />} />
          <Route path="/admin/vocals" element={<Vocals />} />
          <Route path="/admin/prompts" element={<Prompts />} />
          <Route path="/admin/transactions" element={<Transactions />} />
          <Route path="/admin/referrals" element={<Referrals />} />
          <Route path="/admin/stats" element={<Stats />} />
          <Route path="/admin/funnel" element={<Funnel />} />
          <Route path="/admin/utm" element={<UTM />} />
          <Route path="/admin/feedback" element={<Feedback />} />
          <Route path="/admin/timings" element={<Timings />} />
          <Route path="/admin/promo-codes" element={<PromoCodes />} />
          <Route path="/admin/analytics" element={<Analytics />} />
          <Route path="/admin/qr-campaigns" element={<QRCampaigns />} />
          <Route path="/admin/dashboard" element={<AnalyticsDashboard />} />
          <Route path="/admin/settings" element={<Settings />} />
          <Route path="/admin/landing-reviews" element={<LandingReviews />} />
          <Route path="/admin/landing-faq" element={<LandingFAQ />} />
          <Route path="/admin/landing-seo" element={<LandingSEO />} />
          <Route path="/admin/gift-pages" element={<GiftPages />} />
          <Route path="/admin/gift-analytics" element={<GiftAnalytics />} />
          <Route path="/admin/bot-simulator" element={<BotSimulator />} />
          <Route path="/admin/audio-pipeline" element={<AudioPipeline />} />
          <Route path="/admin/post-purchase" element={<PostPurchaseAnalytics />} />
          <Route path="/admin/storage" element={<StorageStats />} />
          <Route path="/admin/broadcasts" element={<Broadcasts />} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;